import { useMemo } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { useIntl } from 'react-intl'

import { isSmallScreen } from '@/constants/breakpoints'
import { BankEligibilityBeneficialOwner } from '@/domains/Business/types'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { DataTableColumnHeader } from '@/shared/ui'

import { OwnerActionCell } from './OwnerActionCell'
import { OwnerAddressCell } from './OwnerAddressCell'
import { OwnerNameCell } from './OwnerNameCell'
import { OwnerRoleCell } from './OwnerRoleCell'
import { OwnerVerificationStateCell } from './OwnerVerificationStateCell'

type Props = {
  onClick: (ownerId: string) => void
}

export function useOwnersAddressTableColumns({
  onClick,
}: Props): ColumnDef<BankEligibilityBeneficialOwner>[] {
  const isMobile = useMediaQuery(isSmallScreen)
  const intl = useIntl()

  const mobileColumns: ColumnDef<BankEligibilityBeneficialOwner>[] = useMemo(
    () => [
      {
        accessorKey: 'firstName',
        cell: ({ row }) => <OwnerNameCell owner={row.original} />,
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={intl.formatMessage({
              id: 'label.keyIndividual',
              defaultMessage: 'Key individual',
            })}
          />
        ),
      },
      {
        id: 'action',
        cell: ({ row }) => (
          <OwnerActionCell owner={row.original} onClick={onClick} />
        ),
      },
    ],
    [intl, onClick],
  )

  const desktopColumns: ColumnDef<BankEligibilityBeneficialOwner>[] = useMemo(
    () => [
      {
        accessorKey: 'firstName',
        cell: ({ row }) => <OwnerNameCell owner={row.original} />,
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={intl.formatMessage({
              id: 'label.keyIndividual',
              defaultMessage: 'Key individual',
            })}
          />
        ),
      },
      {
        accessorKey: 'role',
        cell: () => <OwnerRoleCell />,
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={intl.formatMessage({
              id: 'label.role',
              defaultMessage: 'Role',
            })}
          />
        ),
        size: 90,
      },
      {
        accessorKey: 'addressStreet',
        cell: ({ row }) => <OwnerAddressCell owner={row.original} />,
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={intl.formatMessage({
              id: 'label.residentialAddress',
              defaultMessage: 'Residential address',
            })}
          />
        ),
        size: 270,
      },
      {
        accessorKey: 'status',
        cell: ({ row }) => <OwnerVerificationStateCell owner={row.original} />,
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={intl.formatMessage({
              id: 'label.status',
              defaultMessage: 'Status',
            })}
          />
        ),
        size: 130,
      },
      {
        id: 'action',
        cell: ({ row }) => (
          <OwnerActionCell owner={row.original} onClick={onClick} />
        ),
        size: 90,
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={intl.formatMessage({
              id: 'label.actions',
              defaultMessage: 'Actions',
            })}
          />
        ),
      },
    ],
    [intl, onClick],
  )

  return isMobile ? mobileColumns : desktopColumns
}
